import React, { useState, useEffect, useCallback } from 'react';
import { Brain, ArrowRight, ArrowLeft, CheckCircle, Save, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Alert, ProgressBar, RadioGroup } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Task-specific configuration for Hamilton Anxiety Rating Scale (HAM-A)
export const TaskHamiltonAnxietyConfig = {
  questions: [
    {
      id: 'q1',
      title: 'Anxious Mood',
      description: 'Worries, anticipation of the worst, fearful anticipation, irritability.',
      type: 'rating',
      required: true
    },
    {
      id: 'q2',
      title: 'Tension',
      description: 'Feelings of tension, fatigability, startle response, moved to tears easily, trembling, feelings of restlessness, inability to relax.',
      type: 'rating',
      required: true
    },
    {
      id: 'q3',
      title: 'Fears',
      description: 'Of dark, of strangers, of being left alone, of animals, of traffic, of crowds.',
      type: 'rating',
      required: true
    },
    {
      id: 'q4',
      title: 'Insomnia',
      description: 'Difficulty in falling asleep, broken sleep, unsatisfying sleep and fatigue on waking, dreams, nightmares, night terrors.',
      type: 'rating',
      required: true
    },
    {
      id: 'q5',
      title: 'Intellectual',
      description: 'Difficulty in concentration, poor memory.',
      type: 'rating',
      required: true
    },
    {
      id: 'q6',
      title: 'Depressed Mood',
      description: 'Loss of interest, lack of pleasure in hobbies, depression, early waking, diurnal swing.',
      type: 'rating',
      required: true
    },
    {
      id: 'q7',
      title: 'Somatic (muscular)',
      description: 'Pains and aches, twitching, stiffness, myoclonic jerks, grinding of teeth, unsteady voice, increased muscular tone.',
      type: 'rating',
      required: true
    },
    {
      id: 'q8',
      title: 'Somatic (sensory)',
      description: 'Tinnitus, blurring of vision, hot and cold flushes, feelings of weakness, pricking sensation.',
      type: 'rating',
      required: true
    },
    {
      id: 'q9',
      title: 'Cardiovascular Symptoms',
      description: 'Tachycardia, palpitations, pain in chest, throbbing of vessels, fainting feelings, missing beat.',
      type: 'rating',
      required: true
    },
    {
      id: 'q10',
      title: 'Respiratory Symptoms',
      description: 'Pressure or constriction in chest, choking feelings, sighing, dyspnea.',
      type: 'rating',
      required: true
    },
    {
      id: 'q11',
      title: 'Gastrointestinal Symptoms',
      description: 'Difficulty in swallowing, wind abdominal pain, burning sensations, abdominal fullness, nausea, vomiting, borborygmi, looseness of bowels, loss of weight, constipation.',
      type: 'rating',
      required: true
    },
    {
      id: 'q12',
      title: 'Genitourinary Symptoms',
      description: 'Frequency of micturition, urgency of micturition, amenorrhea, menorrhagia, development of frigidity, premature ejaculation, loss of libido, impotence.',
      type: 'rating',
      required: true
    },
    {
      id: 'q13',
      title: 'Autonomic Symptoms',
      description: 'Dry mouth, flushing, pallor, tendency to sweat, giddiness, tension headache, raising of hair.',
      type: 'rating',
      required: true
    },
    {
      id: 'q14',
      title: 'Behavior at Interview',
      description: 'Fidgeting, restlessness or pacing, tremor of hands, furrowed brow, strained face, sighing or rapid respiration, facial pallor, swallowing, etc.',
      type: 'rating',
      required: true
    }
  ],
  ratingOptions: [
    { value: 0, label: 'Not present' },
    { value: 1, label: 'Mild' },
    { value: 2, label: 'Moderate' },
    { value: 3, label: 'Severe' },
    { value: 4, label: 'Very severe' }
  ]
};

const TaskHamiltonAnxiety = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const questions = TaskHamiltonAnxietyConfig.questions;
  const ratingOptions = TaskHamiltonAnxietyConfig.ratingOptions;

  // Auto-save functionality
  const saveProgress = useCallback(async (questionId, answer, questionIndex) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      const data = await apiPost(
        '/.netlify/functions/hamiltonAnxietyTaskApi',
        {
          sessionId: session.sessionId,
          questionId: questionId,
          answer: answer,
          currentQuestionIndex: questionIndex
        },
        user,
        userInfo?.userId
      );

      setLastSaved(new Date());
      
      // Check if task is completed
      if (data.isCompleted) {
        setShowCompletion(true);
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, task?.taskId, user, userInfo?.userId]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (questionId, answer, questionIndex) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(questionId, answer, questionIndex);
        }, 2000); // 2 seconds for rating scale
      };
    })(),
    [saveProgress]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing answers if session has data
    if (session.sessionData?.answers) {
      setAnswers(session.sessionData.answers);
      setCurrentQuestionIndex(session.sessionData.currentQuestionIndex || 0);
      setShowIntro(false);
    }
  }, [session, hasLoadedProgress]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Auto-save with debouncing
    debouncedSave(questionId, value, currentQuestionIndex);
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Save current answer immediately before moving to next question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id] !== undefined) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on next:', error);
        }
      }
      
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Last question - check if all questions are answered
      const allAnswered = questions.every(q => answers[q.id] !== undefined);
      if (allAnswered) {
        setShowCompletion(true);
      } else {
        toast.error('Please answer all questions before completing the assessment.');
      }
    }
  };

  const handlePrevious = async () => {
    if (currentQuestionIndex > 0) {
      // Save current answer immediately before moving to previous question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id] !== undefined) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on previous:', error);
        }
      }
      
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleManualSave = async () => {
    if (!currentQuestion) return;

    const currentAnswer = answers[currentQuestion.id];
    if (currentAnswer !== undefined) {
      await saveProgress(currentQuestion.id, currentAnswer, currentQuestionIndex);
      toast.success('Progress saved!');
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // Calculate total score
      const totalScore = questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);

      // First, save completion data to the session
      const completionData = {
        answers: answers,
        totalScore: totalScore,
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/hamiltonAnxietyTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        user,
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        user,
        userInfo?.userId
      );

      toast.success('Assessment completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDescription = (questionId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  // Intro Screen
  if (showIntro) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <Brain className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Hamilton Anxiety Rating Scale (HAM-A)</h1>
              <p className="text-gray-600">Assessment of anxiety symptoms</p>
            </div>
          </div>
        </div>

        <Card variant="elevated" padding="large" className="mb-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Assessment Instructions</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              These questions help your therapist understand your symptoms. There are no right or wrong answers. 
              Please answer honestly based on the past week.
            </p>
            <p className="text-gray-600 mb-6">
              You will be asked to rate 14 different aspects of anxiety on a scale from 0 (Not present) to 4 (Very severe).
            </p>
            <Button
              onClick={() => setShowIntro(false)}
              variant="primary"
              size="large"
              className="text-lg font-medium"
            >
              Begin Assessment
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Completion Screen
  if (showCompletion) {
    const totalScore = questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
    
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assessment Complete</h1>
              <p className="text-gray-600">Thank you for completing the Hamilton Anxiety Rating Scale</p>
            </div>
          </div>
        </div>

        <Card variant="elevated" padding="large" className="mb-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Assessment Summary</h2>
            <Alert variant="info" className="mb-6">
              <p className="text-3xl font-bold text-blue-600 mb-2">{totalScore}</p>
              <p className="text-gray-700">Total Score (out of 56)</p>
            </Alert>
            <p className="text-gray-700 mb-6">
              Thanks! Your responses have been saved. Your therapist will review these with you in your next session.
            </p>
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              variant="success"
              size="large"
              className="text-lg font-medium"
            >
              {isSubmitting ? 'Completing...' : 'Complete Assessment'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading assessment questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <Brain className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hamilton Anxiety Rating Scale</h1>
            <p className="text-gray-600">Assessment of anxiety symptoms</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <ProgressBar
          current={currentQuestionIndex + 1}
          total={questions.length}
          variant="blue"
          size="medium"
          showPercentage={true}
          label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
        />
      </div>

      {/* Current Question */}
      <Card variant="elevated" padding="large" className="mb-6">
        <div className="space-y-6">
          {/* Question Title */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {currentQuestion.title}
            </h2>
            
            {/* Collapsible Description */}
            <div className="mb-4">
              <button
                onClick={() => toggleDescription(currentQuestion.id)}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
              >
                {expandedDescriptions[currentQuestion.id] ? (
                  <ChevronUp className="h-4 w-4 mr-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 mr-1" />
                )}
                {expandedDescriptions[currentQuestion.id] ? 'Hide' : 'Show'} description
              </button>
              
              {expandedDescriptions[currentQuestion.id] && (
                <Alert variant="info" className="mt-2">
                  <p className="text-sm text-gray-700">{currentQuestion.description}</p>
                </Alert>
              )}
            </div>
          </div>

          {/* Rating Options */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Rate this symptom based on the past week:
            </p>
            <RadioGroup
              options={ratingOptions.map(option => ({
                value: option.value,
                label: `${option.value} - ${option.label}`
              }))}
              value={answers[currentQuestion.id]}
              onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
              variant="default"
              size="medium"
              name={currentQuestion.id}
            />
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          variant="ghost"
          size="medium"
          className="flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex items-center space-x-4">
          <Button
            onClick={handleManualSave}
            disabled={isSaving || answers[currentQuestion.id] === undefined}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          <Button
            onClick={handleNext}
            disabled={answers[currentQuestion.id] === undefined}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            {isLastQuestion ? 'Review & Complete' : 'Next'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Save Status */}
      {lastSaved && (
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <Clock className="h-4 w-4 mr-2" />
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Helpful Tips */}
      <Alert variant="info" className="mt-8">
        <h3 className="text-sm font-medium text-blue-800 mb-2">💡 Assessment Tips</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Answer based on your experience over the past week</li>
          <li>• There are no right or wrong answers - be honest about your symptoms</li>
          <li>• Your progress is automatically saved as you answer</li>
          <li>• You can navigate between questions freely</li>
          <li>• This assessment helps your therapist understand your anxiety levels</li>
        </ul>
      </Alert>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskHamiltonAnxiety.Summary = ({ task, session }) => {
  const answeredCount = session?.sessionData?.answers ? Object.keys(session.sessionData.answers).length : 0;
  const totalQuestions = TaskHamiltonAnxietyConfig.questions.length;
  
  return (
    <div className="hamilton-anxiety-summary">
      <div className="flex items-center space-x-2">
        <Brain className="h-4 w-4 text-blue-600" />
        <span className="text-xs text-gray-500">
          {answeredCount}/{totalQuestions} questions answered
        </span>
      </div>
    </div>
  );
};

TaskHamiltonAnxiety.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData?.answers) return null;

  const questions = TaskHamiltonAnxietyConfig.questions;
  const totalScore = completionData.totalScore || 0;
  
  return (
    <div className="hamilton-anxiety-results-summary">
      <Card variant="elevated" padding="large" className="mb-4">
        <h3 className="font-medium text-gray-900 mb-2">Hamilton Anxiety Rating Scale Results - {patientName}</h3>
        <Alert variant="info">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600 mb-1">{totalScore}</p>
            <p className="text-sm text-gray-600">Total Score (out of 56)</p>
          </div>
        </Alert>
      </Card>
      
      <div className="space-y-3 text-sm">
        {questions.map(question => (
          <div key={question.id} className="border-l-2 border-blue-200 pl-3">
            <p className="font-medium text-gray-700 mb-1">{question.title}</p>
            <p className="text-gray-600">
              Score: {completionData.answers[question.id] || 0} - {
                TaskHamiltonAnxietyConfig.ratingOptions.find(opt => opt.value === completionData.answers[question.id])?.label || 'Not answered'
              }
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Add static method for task assignment form
TaskHamiltonAnxiety.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  // No custom form needed - return null
  return null;
};

export default TaskHamiltonAnxiety;
